<script setup lang="ts">
  import { toRef, useId } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { UiModalHeader } from '@/shared/ui';
  import EditMemberModalFooter from './EditMemberModalFooter.vue';
  import EditMemberPositionsSection from './EditMemberPositionsSection.vue';
  import EditMemberFunctionalActivitiesSection from './EditMemberFunctionalActivitiesSection.vue';
  import {
    useEditMemberForm,
    type PositionRow,
    type FunctionalActivityRow,
  } from '@/pages/admin/lib/useEditMemberForm';

  const props = defineProps<{
    memberId: number;
  }>();

  const emit = defineEmits<{
    close: [];
    saved: [];
  }>();

  const form = useEditMemberForm(toRef(props, 'memberId'));
  const radioGroupName = `hasFunctionalActivity-${useId()}`;
  const {
    memberName,
    positions,
    functionalActivities,
    hasFunctionalActivity,
    isLoadingMember,
    isSaving,
    loadError,
    isLoadingDepartments,
    isLoadingActivities,
    transformedDepartmentOptions,
    validationMessage,
    isFormValid,
    canAddPosition,
    canAddFunctionalActivity,
  } = form;

  const handleClose = () => {
    emit('close');
  };

  const handleSave = async () => {
    const ok = await form.handleSave();
    if (ok) emit('saved');
  };

  defineExpose({
    testIsMemberFormInteractive: () => !form.isLoadingMember.value && !form.loadError.value,
    testMemberName: () => form.memberName.value,
    testValidationMessage: () => form.validationMessage.value,
    testHandleSave: handleSave,
    testAddPosition: form.addPosition,
    testRemovePosition: form.removePosition,
    testUpdatePositionDepartment: form.updatePositionDepartment,
    testUpdatePositionId: form.updatePositionId,
    testUpdateFunctionalActivity: form.updateFunctionalActivity,
    testAddFunctionalActivity: form.addFunctionalActivity,
    testRemoveFunctionalActivity: form.removeFunctionalActivity,
    testToggleFunctionalActivity: form.toggleFunctionalActivity,
    testHasFunctionalActivity: () => form.hasFunctionalActivity.value,
    testGetPositionOptions: form.getPositionOptions,
    testTransformedDepartmentOptions: () => form.transformedDepartmentOptions.value,
    testIsFormValid: () => form.isFormValid.value,
    testIsSaving: () => form.isSaving.value,
    testSetIsSaving: (v: boolean) => {
      form.isSaving.value = v;
    },
    testHydrateFromMember: form.hydrateFromMember,
    testPositions: () =>
      form.positions.value.map(
        (p): Omit<PositionRow, '_key'> => ({
          departmentId: p.departmentId,
          positionId: p.positionId,
        })
      ),
    testFunctionalActivities: () =>
      form.functionalActivities.value.map(
        (a): Omit<FunctionalActivityRow, '_key'> => ({
          functionalActivityId: a.functionalActivityId,
        })
      ),
    testIsLoadingMember: () => form.isLoadingMember.value,
    testLoadError: () => form.loadError.value,
    testLoadPositionsForDepartment: form.loadPositionsForDepartment,
    testPositionOptionsCache: () => form.positionOptionsCache,
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] max-h-[90vh] shadow-lg overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="flex flex-col flex-1 min-h-0">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02 flex-shrink-0"
        title="Editar membro"
        @close="handleClose"
      />
      <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-x-hidden overflow-y-auto px-6 py-6">
        <div
          v-if="isLoadingMember"
          class="py-12 text-center text-sm text-gray-07"
        >
          Carregando...
        </div>
        <template v-else-if="!loadError">
          <div class="flex-shrink-0">
            <p class="text-sm text-gray-07 font-semibold mb-2">
              Membro:
            </p>
            <p
              class="rounded-lg border border-gray-02 px-4 py-2.5 text-sm text-gray-07 bg-gray-08/30"
            >
              {{ memberName }}
            </p>
          </div>

          <hr class="border-t border-gray-02 flex-shrink-0">

          <EditMemberPositionsSection
            :positions="positions"
            :department-options="transformedDepartmentOptions"
            :is-loading-departments="isLoadingDepartments"
            :can-add-position="canAddPosition"
            :get-position-options="form.getPositionOptions"
            :is-department-loading-positions="form.isDepartmentLoadingPositions"
            @update-department="form.updatePositionDepartment"
            @update-position="form.updatePositionId"
            @add-position="form.addPosition"
            @remove-position="form.removePosition"
          />

          <hr class="border-t border-gray-02 flex-shrink-0">

          <EditMemberFunctionalActivitiesSection
            :has-functional-activity="hasFunctionalActivity"
            :functional-activities="functionalActivities"
            :is-loading-activities="isLoadingActivities"
            :radio-group-name="radioGroupName"
            :can-add-functional-activity="canAddFunctionalActivity"
            :get-filtered-fa-options="form.getFilteredFaOptions"
            @toggle="form.toggleFunctionalActivity"
            @update-activity="form.updateFunctionalActivity"
            @add-activity="form.addFunctionalActivity"
            @remove-activity="form.removeFunctionalActivity"
          />

          <p
            v-if="validationMessage"
            class="text-red-16 text-sm"
          >
            {{ validationMessage }}
          </p>
        </template>
      </div>

      <EditMemberModalFooter
        :is-saving="isSaving"
        :is-form-valid="isFormValid"
        :is-loading-member="isLoadingMember"
        :load-error="loadError"
        @cancel="handleClose"
        @save="handleSave"
      />
    </div>
  </VueFinalModal>
</template>
